import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdArrowBack, MdImage, MdOutlineFormatListBulleted, MdUpload, MdFileDownload } from 'react-icons/md';
import { HiSquares2X2 } from 'react-icons/hi2';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Baseurl } from '../../../../Api/Baseurl';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../../../helpers/ToastOptions';
import Swal from 'sweetalert2';
import { useQuery } from 'react-query';
import Loading from '../../../../components/Loading/Loading';
import { useRoleBasedRoute } from '../../../../utils/routeUtils';
import '../../Modules/FlashcardItems.scss';

const CourseFlashcardItems = () => {
    const { courseId, flashcardSetId } = useParams();
    const token = localStorage.getItem('medtoken');
    const [viewMode, setViewMode] = useState('table');
    const { getRoute } = useRoleBasedRoute();

    // Form states
    const [showAddSingleItem, setShowAddSingleItem] = useState(false);
    const [showEditItem, setShowEditItem] = useState(false);
    const [showExcelUpload, setShowExcelUpload] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [editItemForm, setEditItemForm] = useState({
        id: '',
        question: '',
        answer: '',
        qimage: null
    });

    // Fetch flashcard items
    const fetchFlashcardItems = async () => {
        try {
            const response = await axios.get(`${Baseurl}/admin/flashcard-sets/${flashcardSetId}/items`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error) {
            toast.error('Failed to load flashcard items', ToastOptions('error'));
            throw error || new Error('Failed to load flashcard items');
        }
    };

    const { data: flashcardData = {}, isLoading, refetch, error } = useQuery(
        ["courseFlashcardItems", flashcardSetId],
        fetchFlashcardItems,
        {
            retry: 1,
            onError: (error) => {
                toast.error('Failed to load flashcard items', ToastOptions('error'));
            }
        }
    );

    // Handle different possible data structures
    const items = Array.isArray(flashcardData) ? flashcardData :
        Array.isArray(flashcardData?.data) ? flashcardData.data :
            Array.isArray(flashcardData?.payload) ? flashcardData.payload :
                Array.isArray(flashcardData?.items) ? flashcardData.items : [];

    const count = flashcardData?.count || flashcardData?.total || items.length || 0;

    // Toggle view mode
    const toggleViewMode = (mode) => {
        setViewMode(mode);
    };

    // Add Single Flashcard Item Formik
    const addSingleItemValidation = Yup.object({
        question: Yup.string().required('Question is required'),
        answer: Yup.string().required('Answer is required'),
        qimage: Yup.mixed().nullable()
    });

    const addSingleItemFormik = useFormik({
        initialValues: {
            question: '',
            answer: '',
            qimage: null
        },
        validationSchema: addSingleItemValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const formData = new FormData();
                formData.append('question', values.question);
                formData.append('answer', values.answer);
                if (values.qimage) {
                    formData.append('qimage', values.qimage);
                }

                const response = await axios.post(`${Baseurl}/admin/flashcard-sets/${flashcardSetId}/item`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.msg === 'Flashcard item created successfully') {
                    toast.success('Flashcard item added successfully', ToastOptions('success'));
                    setShowAddSingleItem(false);
                    resetForm();
                    refetch();
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to add flashcard item', ToastOptions('error'));
            }
        }
    });

    // Excel Upload Formik
    const excelUploadValidation = Yup.object({
        file: Yup.mixed().required('Excel file is required')
    });

    const excelUploadFormik = useFormik({
        initialValues: {
            file: null
        },
        validationSchema: excelUploadValidation,
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                const formData = new FormData();
                formData.append('file', values.file);

                const response = await axios.post(`${Baseurl}/admin/flashcard-sets/${flashcardSetId}/items/excel`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.msg) {
                    toast.success(response.data.msg, ToastOptions('success'));
                    setShowExcelUpload(false);
                    resetForm();
                    refetch();
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to upload Excel file', ToastOptions('error'));
            } finally {
                setSubmitting(false);
            }
        }
    });

    // Edit Flashcard Item Formik
    const editItemValidation = Yup.object({
        question: Yup.string().required('Question is required'),
        answer: Yup.string().required('Answer is required'),
        qimage: Yup.mixed().nullable()
    });

    const editItemFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            question: editItemForm.question || '',
            answer: editItemForm.answer || '',
            qimage: null
        },
        validationSchema: editItemValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const formData = new FormData();
                formData.append('question', values.question);
                formData.append('answer', values.answer);
                if (values.qimage) {
                    formData.append('qimage', values.qimage);
                }

                const response = await axios.put(`${Baseurl}/admin/flashcard-items/${editItemForm.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.msg === 'Flashcard item updated successfully') {
                    toast.success('Flashcard item updated successfully', ToastOptions('success'));
                    setShowEditItem(false);
                    setEditItemForm({ id: '', question: '', answer: '', qimage: null });
                    resetForm();
                    refetch();
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to update flashcard item', ToastOptions('error'));
            }
        }
    });

    // Delete flashcard item
    const handleDeleteItem = async (itemId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This flashcard item will be permanently deleted",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#01677e",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${Baseurl}/admin/flashcard-items/${itemId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Flashcard item deleted successfully', ToastOptions('success'));
                    refetch();
                } catch (error) {
                    toast.error('Failed to delete flashcard item', ToastOptions('error'));
                }
            }
        });
    };

    // Export flashcard items to Excel
    const handleExportToExcel = async () => {
        if (items.length === 0) {
            toast.error('No flashcard items to export', ToastOptions('error'));
            return;
        }

        setIsExporting(true);
        
        try {
            // Show loading toast
            const loadingToast = toast.loading('Preparing export...', ToastOptions('info'));
            
            // Simulate processing time for better UX
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Create Excel data
            const excelData = items.map((item, index) => ({
                'No.': index + 1,
                'Question': item.question,
                'Answer': item.answer,
                'Has Image': item.qimage ? 'Yes' : 'No'
            }));

            // Convert to CSV format
            const headers = ['No.', 'Question', 'Answer', 'Has Image'];
            const csvContent = [
                headers.join(','),
                ...excelData.map(row => 
                    headers.map(header => 
                        `"${(row[header] || '').toString().replace(/"/g, '""')}"`
                    ).join(',')
                )
            ].join('\n');

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `flashcard-items-${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up URL object
            URL.revokeObjectURL(url);
            
            // Dismiss loading toast and show success
            toast.dismiss(loadingToast);
            toast.success(`Successfully exported ${items.length} flashcard items`, ToastOptions('success'));
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export flashcard items', ToastOptions('error'));
        } finally {
            setIsExporting(false);
        }
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div className="flashcard-items">
            <div className="content-header">
                <div className="header-left">
                    <Link to={getRoute(`courses/${courseId}/content`)} className="back-btn">
                        <MdArrowBack /> Back to Course Content
                    </Link>
                    <div>
                        <h2>Flashcard Items</h2>
                        <p>Manage flashcard items in this set ({count} items)</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button 
                        onClick={handleExportToExcel} 
                        className="export-btn"
                        disabled={isExporting || items.length === 0}
                        title={items.length === 0 ? 'No items to export' : 'Export flashcard items to CSV'}
                    >
                        {isExporting ? (
                            <>
                                <div className="spinner"></div>
                                Exporting...
                            </>
                        ) : (
                            <>
                                <MdFileDownload />
                                Export ({items.length})
                            </>
                        )}
                    </button>
                    <button onClick={() => setShowExcelUpload(true)} className="excel-upload-btn">
                        <MdUpload /> Upload Excel
                    </button>
                    <button onClick={() => setShowAddSingleItem(true)} className="add-item-btn">
                        <MdAdd /> Add Single Item
                    </button>
                </div>
            </div>

            {/* View Toggle */}
            <div className="view-toggle">
                <div className="toggle-buttons">
                    <button
                        className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                    >
                        <HiSquares2X2 />
                        Cards View
                    </button>
                    <button
                        className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                    >
                        <MdOutlineFormatListBulleted />
                        Table View
                    </button>
                </div>
            </div>

            {/* Cards View */}
            {viewMode === 'cards' && (
                <div className="items-grid">
                    {items.length === 0 ? (
                        <div className="empty-state">
                            <div className="nodatayet-message">No data yet</div>
                            <button className="add-item-btn-empty" onClick={() => setShowAddSingleItem(true)}>
                                + Add Item
                            </button>
                        </div>
                    ) : (
                        items.map((item, index) => (
                            <div key={item.id} className="flashcard-item">
                                <div className="card-number">#{index + 1}</div>
                                <div className="card-content">
                                    <div className="question-section">
                                        <h3>Question</h3>
                                        <p>{item.question}</p>
                                        <div className="question-image">
                                            {item.qimage ? (
                                                <img src={`${Baseurl}/${item.qimage}`} alt="Question" />
                                            ) : (
                                                <div className="image-placeholder">
                                                    <MdImage />
                                                    <span>No Image</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="answer-section">
                                        <h3>Answer</h3>
                                        <p>{item.answer}</p>
                                    </div>
                                </div>
                                <div className="card-actions">
                                    <button
                                        className="edit-btn"
                                        onClick={() => {
                                            setEditItemForm({
                                                id: item.id,
                                                question: item.question,
                                                answer: item.answer,
                                                qimage: null
                                            });
                                            setShowEditItem(true);
                                        }}
                                    >
                                        <MdEdit />
                                    </button>
                                    <button
                                        className="delete-btn"
                                        onClick={() => handleDeleteItem(item.id)}
                                    >
                                        <MdDelete />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <div className="table-view-container">
                    <div style={{ padding: '10px', background: '#f0f0f0', margin: '10px', border: '1px solid #ccc' }}>
                        <p>Items count: {items.length}</p>
                    </div>
                    <div className="overflow-x-auto shadow-md sm:rounded-lg">
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3">#</th>
                                    <th scope="col" className="px-6 py-3">Question</th>
                                    <th scope="col" className="px-6 py-3">Answer</th>
                                    <th scope="col" className="px-6 py-3">Image</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                                <span className="ml-2">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center">
                                            <div className="py-8">
                                                <div className="text-red-500">
                                                    <p>Error loading flashcard items</p>
                                                    <p className="text-sm text-gray-500">{error?.message || 'Unknown error occurred'}</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : items && items.length > 0 ? (
                                    items.map((item, index) => (
                                        <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                            <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-xs">
                                                    <p className="text-gray-900 dark:text-white">{item.question}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-xs">
                                                    <p className="text-gray-900 dark:text-white">{item.answer}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {item.qimage ? (
                                                    <div className="flex justify-center">
                                                        <img
                                                            src={`${Baseurl}/${item.qimage}`}
                                                            alt="Question"
                                                            className="w-16 h-12 object-cover rounded border"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center">
                                                        <div className="table-image-placeholder">
                                                            <MdImage />
                                                            <span>No Image</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 svg-del">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditItemForm({
                                                                id: item.id,
                                                                question: item.question,
                                                                answer: item.answer,
                                                                qimage: null
                                                            });
                                                            setShowEditItem(true);
                                                        }}
                                                    >
                                                        <MdEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteItem(item.id)}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center">
                                            <div className="py-8">
                                                <div className="text-gray-500">
                                                    <p>No flashcard items found</p>
                                                    <p className="text-sm">Add some items to get started</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Add Single Item Modal */}
            {showAddSingleItem && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Add Flashcard Item</h3>
                        <form onSubmit={addSingleItemFormik.handleSubmit}>
                            <div className="form-group">
                                <label>Question</label>
                                <input
                                    type="text"
                                    name="question"
                                    placeholder="Enter the question"
                                    value={addSingleItemFormik.values.question}
                                    onChange={addSingleItemFormik.handleChange}
                                    onBlur={addSingleItemFormik.handleBlur}
                                />
                                {addSingleItemFormik.touched.question && addSingleItemFormik.errors.question && (
                                    <div className="error">{addSingleItemFormik.errors.question}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Answer</label>
                                <textarea
                                    name="answer"
                                    placeholder="Enter the answer"
                                    value={addSingleItemFormik.values.answer}
                                    onChange={addSingleItemFormik.handleChange}
                                    onBlur={addSingleItemFormik.handleBlur}
                                />
                                {addSingleItemFormik.touched.answer && addSingleItemFormik.errors.answer && (
                                    <div className="error">{addSingleItemFormik.errors.answer}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Question Image (Optional)</label>
                                <div className="file-upload">
                                    <input
                                        type="file"
                                        name="qimage"
                                        accept="image/*"
                                        onChange={(e) => addSingleItemFormik.setFieldValue('qimage', e.target.files[0])}
                                        id="single-item-image"
                                    />
                                    <label htmlFor="single-item-image" className="file-label">
                                        <MdImage />
                                        <span>Choose Image</span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="submit">Add Item</button>
                                <button type="button" onClick={() => setShowAddSingleItem(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Excel Upload Modal */}
            {showExcelUpload && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Upload Excel File</h3>
                        <div className="excel-upload-info">
                            <p>Upload an Excel file to create multiple flashcard items at once.</p>
                            <div className="excel-requirements">
                                <h4>Excel File Requirements:</h4>
                                <ul>
                                    <li>Required columns: <strong>question</strong>, <strong>answer</strong></li>
                                    <li>File format: .xlsx or .xls</li>
                                </ul>
                            </div>
                        </div>
                        <form onSubmit={excelUploadFormik.handleSubmit}>
                            <div className="form-group">
                                <label>Excel File</label>
                                <div className="file-upload">
                                    <input
                                        type="file"
                                        name="file"
                                        accept=".xlsx,.xls"
                                        onChange={(e) => excelUploadFormik.setFieldValue('file', e.target.files[0])}
                                        id="excel-file"
                                    />
                                    <label htmlFor="excel-file" className="file-label">
                                        <MdUpload />
                                        <span>Choose Excel File</span>
                                    </label>
                                </div>
                                {excelUploadFormik.touched.file && excelUploadFormik.errors.file && (
                                    <div className="error">{excelUploadFormik.errors.file}</div>
                                )}
                                {excelUploadFormik.values.file && (
                                    <div className="file-selected">
                                        <span>Selected: {excelUploadFormik.values.file.name}</span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="modal-actions">
                                <button type="submit" disabled={excelUploadFormik.isSubmitting}>
                                    {excelUploadFormik.isSubmitting ? 'Uploading...' : 'Upload & Create Items'}
                                </button>
                                <button type="button" onClick={() => setShowExcelUpload(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Item Modal */}
            {showEditItem && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit Flashcard Item</h3>
                        <form onSubmit={editItemFormik.handleSubmit}>
                            <div className="form-group">
                                <label>Question</label>
                                <input
                                    type="text"
                                    name="question"
                                    placeholder="Enter the question"
                                    value={editItemFormik.values.question}
                                    onChange={editItemFormik.handleChange}
                                    onBlur={editItemFormik.handleBlur}
                                />
                                {editItemFormik.touched.question && editItemFormik.errors.question && (
                                    <div className="error">{editItemFormik.errors.question}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Answer</label>
                                <textarea
                                    name="answer"
                                    placeholder="Enter the answer"
                                    value={editItemFormik.values.answer}
                                    onChange={editItemFormik.handleChange}
                                    onBlur={editItemFormik.handleBlur}
                                />
                                {editItemFormik.touched.answer && editItemFormik.errors.answer && (
                                    <div className="error">{editItemFormik.errors.answer}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Question Image (Optional)</label>
                                <div className="file-upload">
                                    <input
                                        type="file"
                                        name="qimage"
                                        accept="image/*"
                                        onChange={(e) => editItemFormik.setFieldValue('qimage', e.target.files[0])}
                                        id="edit-item-image"
                                    />
                                    <label htmlFor="edit-item-image" className="file-label">
                                        <MdImage />
                                        <span>Choose New Image</span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="submit">Save Changes</button>
                                <button type="button" onClick={() => setShowEditItem(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseFlashcardItems; 